import { useState } from 'react';

// MCP endpoint URL (mcp.thedraftlion.com/mcp). Injected at build time so
// it's the same in dev and prod.
const MCP_URL: string = import.meta.env.VITE_MCP_URL ?? 'https://mcp.thedraftlion.com/mcp';

type AI = 'claude' | 'chatgpt';

export default function Setup() {
  const [ai, setAi] = useState<AI>('claude');

  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <h1 className="text-3xl md:text-4xl font-bold">Setup guide</h1>
      <p className="mt-4 text-slate-300">
        New to all this? You're in the right place. We'll walk you through it line by line.
        Most people finish in under 5 minutes — and the only credential you need is the
        email you used at checkout.
      </p>

      {/* AI toggle */}
      <div className="mt-8">
        <p className="text-sm text-slate-400 mb-2">Which AI tool are you using?</p>
        <div className="inline-flex rounded-lg border border-slate-800 p-1">
          <button
            onClick={() => setAi('claude')}
            className={`px-4 py-2 rounded-md text-sm transition ${
              ai === 'claude' ? 'bg-amber-500 text-slate-950 font-semibold' : 'text-slate-300 hover:text-white'
            }`}
          >
            Claude
          </button>
          <button
            onClick={() => setAi('chatgpt')}
            className={`px-4 py-2 rounded-md text-sm transition ${
              ai === 'chatgpt' ? 'bg-amber-500 text-slate-950 font-semibold' : 'text-slate-300 hover:text-white'
            }`}
          >
            ChatGPT
          </button>
        </div>
      </div>

      {ai === 'claude' ? <ClaudeSteps/> : <ChatGPTSteps />}

      <p className="mt-16 text-center text-slate-400">
        That's it. Good luck this draft season. 🏆
      </p>
    </div>
  );
}

// ==================================================================
// CLAUDE FLOW
// ==================================================================
function ClaudeSteps() {
  return (
    <>
      {/* Prerequisites */}
      <Section title="Before you start">
        <p>You'll need two things — both quick.</p>
        <ul className="mt-4 space-y-2">
          <Check>
            <strong>Claude Desktop</strong> (or Claude.ai in your browser) signed
            in to a <strong>Pro, Max, Team, or Enterprise plan</strong>.{' '}
            <a className="text-amber-400 hover:text-amber-300" href="https://claude.ai/download" target="_blank" rel="noopener">Download Claude Desktop</a> if you don't have it.
          </Check>
          <Check>
            <strong>The email you used at checkout</strong>. There's no API key to copy or
            password to set — when you connect, we'll email you a one-click sign-in link.
          </Check>
        </ul>

        <Callout type="warning">
          <strong>One thing to plan for:</strong> Claude itself has usage limits. We strongly
          recommend <strong>Claude Pro</strong> (or higher) during draft season, and doing a
          quick test run an hour before your draft so you know you're set.
        </Callout>
      </Section>

      {/* Step 1 */}
      <Section title="Step 1 — Open Connector settings">
        <ol className="mt-2 space-y-3 text-slate-300">
          <li><span className="text-amber-400 font-mono mr-2">1.</span> Open <strong>Claude Desktop</strong> (or go to <a className="text-amber-400 hover:text-amber-300" href="https://claude.ai" target="_blank" rel="noopener">claude.ai</a> in your browser).</li>
          <li>
            <span className="text-amber-400 font-mono mr-2">2.</span>{' '}
              <>Click <strong>Customize</strong> in the left hand pane above the recent chat history, then click <strong>Connectors</strong></>
          </li>
          <li><span className="text-amber-400 font-mono mr-2">3.</span> In the window, click <strong>the plus sign (+)</strong> then <strong>Add custom connector</strong></li>
        </ol>

        <Callout type="info">
          <strong>Don't see "Connectors"?</strong> Make sure Claude Desktop is up to date —
          remote connectors were added in a recent release. On the web, go to{' '}
          <a className="text-amber-400 hover:text-amber-300" href="https://claude.ai/settings/connectors" target="_blank" rel="noopener">claude.ai/settings/connectors</a> directly.
        </Callout>
      </Section>

      {/* Step 2 */}
      <Section title="Step 2 — Add DraftLion as a custom connector">
        <p>Fill in the form using the values below.</p>

        <div className="mt-6 rounded-lg border border-slate-800 bg-slate-900/40 p-5 text-sm">
          <Field label="Name">
            <CodeBlock>DraftLion</CodeBlock>
          </Field>
          <Field label="Remote MCP server URL">
            <CodeBlock>{MCP_URL}</CodeBlock>
          </Field>
        </div>

        <p className="mt-6">
          Click <strong>Add</strong> (or <strong>Save</strong> — the label varies by version).
          Claude will discover that DraftLion uses OAuth and immediately open a browser
          window for sign-in. Move to Step 3.
        </p>
      </Section>

      {/* Step 3 — Sign in */}
      <Section title="Step 3 — Sign in (first time only, ~30 seconds)">
        <ol className="mt-2 space-y-3 text-slate-300">
          <li><span className="text-amber-400 font-mono mr-2">1.</span> Enter the <strong>email you used at checkout</strong> on the sign-in page that opens.</li>
          <li><span className="text-amber-400 font-mono mr-2">2.</span> Check your inbox — we'll email you a one-click sign-in link (expires in 15 min).</li>
          <li><span className="text-amber-400 font-mono mr-2">3.</span> Click the link. The browser confirms you're signed in, then hands control back to Claude.</li>
          <li><span className="text-amber-400 font-mono mr-2">4.</span> Make sure DraftLion shows as <strong>Connected</strong> in the connectors list.</li>
          <li><span className="text-amber-400 font-mono mr-2">5.</span> In the DraftLions tool that now shows, give each tool <strong>Always Allow</strong> permission.
          </li>
        </ol>
        <p className="mt-4 text-sm text-slate-400">
          You'll only do this once per device — Claude remembers the connection after that.
        </p>

        <Callout type="info">
          <strong>Sign-in tab didn't open within 30 seconds?</strong> This sometimes
          happens on the first connect — Claude's connector handshake can stall
          before launching your browser. Fix: fully quit Claude
          (<code className="font-mono text-sm bg-slate-800 px-1.5 py-0.5 rounded">⌘+Q</code> on Mac,
          right-click tray icon → Quit on Windows), reopen
          it, and click <strong>Connect</strong> on DraftLion in your connectors list.
          The sign-in tab should open immediately.
        </Callout>
      </Section>

      {/* Step 4 — Test */}
      <Section title="Step 4 — Test it works">
        <p>
          Open any conversation in Claude.
        </p>
        <CodeBlock>My sleeper username is "REPLACE WITH YOUR USERNAME". What leagues am I in?</CodeBlock>
        <p className="mt-4 text-slate-300">
          You should see Claude think for a second, indicate it's calling a connector,
          then respond with a list of your leagues. That's DraftLion talking to Sleeper through Claude.
        </p>

        <Callout type="info">
          <strong>Tool not getting called?</strong> Confirm DraftLion is toggled on for the
          chat. You can also nudge Claude once — "Use the DraftLion connector to check
          trending adds" — and it'll keep using it for the rest of the conversation.
        </Callout>
      </Section>

      {/* Troubleshooting */}
      <Section title="If something goes wrong">
        <p className="text-slate-300">
          The fixes below cover ~90% of issues. If none of them work, the last item tells
          you exactly what to send us so we can fix it for you.
        </p>

        <Faq q="I don't see 'Add custom connector' anywhere">
          Custom remote connectors require Claude Pro, Max, Team, or Enterprise — they're
          not available on the Free plan. They also require a recent Claude Desktop build,
          so make sure you're on the latest version (or use claude.ai in your browser).
        </Faq>

        <Faq q="The sign-in browser window never opened">
          Two likely causes, in order of how often they happen:
          <ol className="mt-2 ml-6 list-decimal space-y-2 text-sm">
            <li>
              <strong>Claude's connector handshake stalled on first connect.</strong>{' '}
              Fully quit Claude
              (<code className="font-mono text-sm bg-slate-800 px-1.5 py-0.5 rounded">⌘+Q</code> on Mac,
              right-click tray icon → Quit on Windows) and reopen it. Click{' '}
              <strong>Connect</strong> on DraftLion again — the sign-in tab should
              open immediately. You only need to do this once per device.
            </li>
            <li>
              <strong>Browser blocked the popup.</strong> Look for a "popup blocked"
              icon in your browser's address bar and allow popups for thedraftlion.com,
              then click <strong>Connect</strong> again.
            </li>
          </ol>
        </Faq>

        <Faq q="The sign-in email never arrived">
          Check your spam folder first. If still nothing after 2 minutes, double-check
          you're using the same email as your Stripe receipt. You can verify your email
          (and account status) at <a className="text-amber-400 hover:text-amber-300" href="/manage">Manage</a>.
        </Faq>

        <Faq q="I get an 'Unauthorized' or '401' error">
          Your sign-in expired or wasn't completed. In Settings → Connectors, find
          DraftLion and click <strong>Reconnect</strong> — you'll be sent through the
          email sign-in flow again.
        </Faq>

        <Faq q="DraftLion's tools don't appear in Claude after connecting">
          Two things to try:
          <ol className="mt-2 ml-6 list-decimal space-y-1 text-sm">
            <li>Confirm DraftLion is toggled on for the current chat (look near the message input for the tools/connectors menu).</li>
            <li>Start a new chat. Some Claude versions need a fresh conversation to pick up newly-added connectors.</li>
          </ol>
        </Faq>

        <Faq q="None of the above worked — how do I get help?">
          Email <a className="text-amber-400 hover:text-amber-300" href="mailto:support@thedraftlion.com">support@thedraftlion.com</a> and include:
          <ul className="mt-2 ml-6 list-disc space-y-1 text-sm">
            <li>Your Claude plan (Pro / Max / Team / Enterprise)</li>
            <li>Which step you got stuck on</li>
            <li>A screenshot of the error or connector screen</li>
          </ul>
          <p className="mt-3">
            We reply within 24 hours, faster during draft season.
          </p>
        </Faq>
      </Section>
    </>
  );
}

// ==================================================================
// CHATGPT FLOW
// ==================================================================
function ChatGPTSteps() {
  return (
    <>
      {/* Prerequisites */}
      <Section title="Before you start">
        <p>You'll need two things — both quick.</p>
        <ul className="mt-4 space-y-2">
          <Check>
            <strong>ChatGPT in a web browser</strong>, signed in to a{' '}
            <strong>Plus, Pro, or Business plan</strong>. Two things to know: the{' '}
            <strong>desktop app can't install custom MCP servers</strong>, so this
            has to be done on the web — and custom connectors aren't available on
            the free tier.{' '}
            <a className="text-amber-400 hover:text-amber-300" href="https://chatgpt.com" target="_blank" rel="noopener">
              Open ChatGPT
            </a>.
          </Check>
          <Check>
            <strong>The email you used at checkout</strong>. There's no API key to
            paste — when you add the connector, your browser will pop open and we'll
            email you a one-click sign-in link.
          </Check>
        </ul>

        <Callout type="warning">
          <strong>One thing to plan for:</strong> ChatGPT has its own message limits per plan.
          During draft season we strongly recommend <strong>ChatGPT Plus or Pro</strong>, and
          doing a 5-minute test run an hour before your draft so you know you're set.
        </Callout>
      </Section>

      {/* Step 1 */}
      <Section title="Step 1 — Turn on Developer Mode in ChatGPT">
        <ol className="mt-2 space-y-3 text-slate-300">
          <li>
            <span className="text-amber-400 font-mono mr-2">1.</span> Open{' '}
            <a className="text-amber-400 hover:text-amber-300" href="https://chatgpt.com" target="_blank" rel="noopener">
              chatgpt.com
            </a>{' '}in your web browser — <strong>not the desktop app</strong>, which
            can't install custom MCP servers.
          </li>
          <li>
            <span className="text-amber-400 font-mono mr-2">2.</span> Click your{' '}
            <strong>profile picture</strong> (bottom-left corner) and choose <strong>Settings</strong>.
          </li>
          <li>
            <span className="text-amber-400 font-mono mr-2">3.</span> In the Settings window,
            click <strong>Plugins</strong>.
          </li>
          <li>
            <span className="text-amber-400 font-mono mr-2">4.</span> Scroll to the bottom,
            find <strong>Developer Mode</strong>, and toggle it on. This is required to add
            custom MCP servers — you can turn it back off when you're not using DraftLion.
          </li>
        </ol>

        <Callout type="info">
          <strong>Don't see "Plugins"?</strong> The label is sometimes "Apps & connectors"
          or sits under <strong>Beta features</strong>. If you're on a free plan, you'll need
          to upgrade first — apps are a paid feature in ChatGPT.
        </Callout>
      </Section>

      {/* Step 2 */}
      <Section title="Step 2 — Add DraftLion as a Plugin">
        <ol className="mt-2 space-y-3 text-slate-300">
          <li>
            <span className="text-amber-400 font-mono mr-2">1.</span> Go back to{' '}
            <strong>Plugins</strong>, then click <strong>Browse Plugins</strong>.
          </li>
          <li>
            <span className="text-amber-400 font-mono mr-2">2.</span> Click the{' '}
            <strong>+</strong> sign near the upper-right corner.
          </li>
          <li>
            <span className="text-amber-400 font-mono mr-2">3.</span> Fill in the form using
            the values below, then click Create.
          </li>
        </ol>

        <div className="mt-6 rounded-lg border border-slate-800 bg-slate-900/40 p-5 text-sm">
          <Field label="Name">
            <CodeBlock>DraftLion</CodeBlock>
          </Field>
          <Field label="Description (optional)">
            <CodeBlock>Fantasy football draft + trade assistant</CodeBlock>
          </Field>
          <Field label="MCP Server URL">
            <CodeBlock>{MCP_URL}</CodeBlock>
          </Field>
          <Field label="Authentication">
            <p className="text-slate-300">
              Choose <strong>OAuth</strong> (sometimes labeled <strong>OAuth 2.0</strong>{' '}
              or <strong>Sign in</strong>). Leave any "client ID / secret" fields blank —
              ChatGPT will register itself automatically.
            </p>
            <p className="mt-2 text-slate-400">
              When you save, your browser will pop open asking for your DraftLion email.
              Enter it, click the magic link in your inbox, and ChatGPT takes it from there.
            </p>
          </Field>
        </div>

        <Callout type="warning">
          <strong>Heads up:</strong> ChatGPT's connector UI changes every few weeks. If a label
          on your screen doesn't match exactly, look for the closest equivalent. Or email{' '}
          <a className="text-amber-400 hover:text-amber-300" href="mailto:support@thedraftlion.com">
            support@thedraftlion.com
          </a>{' '}with a screenshot and we'll point you to the right spot.
        </Callout>
      </Section>

      {/* Step 3 */}
      <Section title="Step 3 — Sign in via the browser popup">
        <ol className="mt-2 space-y-3 text-slate-300">
          <li>
            <span className="text-amber-400 font-mono mr-2">1.</span> Click <strong>Save</strong>{' '}
            (or <strong>Create connector</strong>). A browser tab opens.
          </li>
          <li>
            <span className="text-amber-400 font-mono mr-2">2.</span> Enter the{' '}
            <strong>email you used at checkout</strong> and click "Email me a sign-in link".
          </li>
          <li>
            <span className="text-amber-400 font-mono mr-2">3.</span> Open the email
            (it arrives within a minute — peek in spam if not), click the link.
          </li>
          <li>
            <span className="text-amber-400 font-mono mr-2">4.</span> The browser confirms
            sign-in and hands control back to ChatGPT. Make sure DraftLion is{' '}
            <strong>toggled on</strong> in the connectors list.
          </li>
        </ol>

        <Callout type="info">
          <strong>Sign-in tab didn't open within 30 seconds?</strong> This sometimes
          happens on the first connect — the connector handshake can stall before
          launching your browser. Fix: fully quit ChatGPT Desktop, reopen it, and
          click <strong>Connect</strong> on DraftLion in your connectors list. The
          sign-in tab should open immediately.
        </Callout>
      </Section>

      {/* Step 4 */}
      <Section title="Step 4 — Test it works">
        <p>
          Start a new ChatGPT chat. Look for a small icon below the message input (usually a{' '}
          <strong>+</strong> or <strong>tools</strong> icon) — make sure DraftLion is enabled
          for this chat. Then ask:
        </p>
        <CodeBlock>What are the trending fantasy football adds in the last 24 hours?</CodeBlock>
        <p className="mt-4 text-slate-300">
          You should see ChatGPT think for a second, indicate it's calling a connector, then
          respond with a list your leagues. That's DraftLion talking to Sleeper through ChatGPT.
        </p>

        <Callout type="info">
          <strong>Tool not getting called?</strong> Some ChatGPT versions require you to
          explicitly enable connectors per chat. If you don't see DraftLion being used in
          its response, try: "Use the DraftLion connector and tell me trending fantasy
          football adds." If that works, the connector is fine — ChatGPT just needed a nudge.
        </Callout>
      </Section>

      {/* Troubleshooting */}
      <Section title="If something goes wrong">
        <Faq q="ChatGPT says it can't connect to the server">
          Most often a typo in the URL. Re-check that the MCP server URL exactly matches
          the one in Step 2 (no trailing slash, no missing characters).
        </Faq>

        <Faq q="I don't see a Connectors option in Settings">
          ChatGPT MCP connectors require Plus, Pro, or Business. Free-tier accounts
          don't have this option. If you're on a paid plan and still don't see it,
          try updating ChatGPT Desktop to the latest version — the menu was added
          fairly recently.
        </Faq>

        <Faq q="The browser sign-in popup never opened">
          Two likely causes, in order of how often they happen:
          <ol className="mt-2 ml-6 list-decimal space-y-2 text-sm">
            <li>
              <strong>Connector handshake stalled on first connect.</strong> Fully
              quit ChatGPT Desktop, reopen it, and click <strong>Connect</strong>{' '}
              on DraftLion again — the sign-in tab should open immediately. You only
              need to do this once per device.
            </li>
            <li>
              <strong>Browser blocked the popup.</strong> Look for a "popup blocked"
              icon in your browser's address bar, allow popups for thedraftlion.com,
              and click <strong>Connect</strong> again.
            </li>
          </ol>
        </Faq>

        <Faq q="The sign-in email never arrived">
          Check your spam folder first. If still nothing after 2 minutes, double-check
          you're using the email you used at checkout (look at any past Stripe receipt).
          You can also verify your email at <a className="text-amber-400 hover:text-amber-300" href="/manage">Manage</a>.
        </Faq>

        <Faq q="ChatGPT replies but doesn't call DraftLion">
          Two things to try:
          <ol className="mt-2 ml-6 list-decimal space-y-1 text-sm">
            <li>Confirm DraftLion is toggled on in the connectors list.</li>
            <li>In your prompt, mention DraftLion by name once: "Use DraftLion to
            check trending fantasy football adds." After that ChatGPT usually
            keeps using it for the rest of the chat.</li>
          </ol>
        </Faq>

        <Faq q="None of the above worked — how do I get help?">
          Email <a className="text-amber-400 hover:text-amber-300" href="mailto:support@thedraftlion.com">support@thedraftlion.com</a> and include:
          <ul className="mt-2 ml-6 list-disc space-y-1 text-sm">
            <li>Your ChatGPT plan (Plus / Pro / Business)</li>
            <li>Which step you got stuck on</li>
            <li>A screenshot of the connector setup screen</li>
          </ul>
          <p className="mt-3">
            We reply within 24 hours, faster during draft season.
          </p>
        </Faq>
      </Section>
    </>
  );
}

// ------------------------------------------------------------------
// UI primitives
// ------------------------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-12 pt-12 border-t border-slate-800 first:border-0 first:pt-0 first:mt-12">
      <h2 className="text-xl md:text-2xl font-bold text-white">{title}</h2>
      <div className="mt-4 space-y-4 text-slate-300 leading-relaxed">{children}</div>
    </section>
  );
}

function CodeBlock({ children }: { children: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard not available */ }
  }
  return (
    <div className="relative my-4">
      <pre className="code-block whitespace-pre-wrap break-all">{children}</pre>
      <button
        onClick={copy}
        className="absolute top-3 right-3 text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300"
      >
        {copied ? 'Copied!' : 'Copy'}
      </button>
    </div>
  );
}

function Check({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="text-amber-400 mt-1" aria-hidden>✓</span>
      <span>{children}</span>
    </li>
  );
}

function Callout({ type, children }: { type: 'warning' | 'info'; children: React.ReactNode }) {
  const ring = type === 'warning' ? 'border-amber-500/50 bg-amber-500/5' : 'border-blue-500/50 bg-blue-500/5';
  return (
    <div className={`my-6 rounded-lg border ${ring} p-4 text-sm`}>{children}</div>
  );
}

function Faq({ q, children }: { q: string; children: React.ReactNode }) {
  return (
    <div className="mt-4">
      <h3 className="font-semibold text-white">{q}</h3>
      <div className="mt-1 text-sm text-slate-400">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-4 last:mb-0">
      <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1">{label}</p>
      <div>{children}</div>
    </div>
  );
}
